/**
 * Sessions — the unit a volunteer is assigned to and records hours against.
 *
 * Read-only from the portal for now. Sessions are created with their activity,
 * and nothing on these screens edits one yet.
 *
 * This was `phases.ts` until 13 Sep 2026, when phases were removed and the
 * chain became program → activity → session.
 */

import type { AssignmentDetail, CreateSessionBody } from '@parinaam-vms/shared';
import { apiDelete, apiGet, apiGetPage, apiPatch, apiPost, apiPut, toQueryString } from './client';
import type { Page } from './client';

export interface Session {
  session_id: string;
  activity_id: string;
  program_id: string;

  session_name: string;
  start_date: string;
  /** Null when the session finishes on `start_date` rather than over a range. */
  end_date: string | null;
  responsibility: 'PARINAAM' | 'VOLUNTEER' | 'COLLABORATIVE';
  /**
   * Wider than the three states a phase had: a session can be drafted before
   * it is scheduled, and cancelled outright.
   */
  status: 'DRAFT' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

  description: string | null;

  parinaam_confirmed_at: string | null;
  parinaam_confirmed_by: string | null;
  volunteer_confirmed_at: string | null;
  volunteer_confirmed_by: string | null;

  status_override: boolean;
  override_reason: string | null;

  /** The Parinaam team member responsible, and their name. Null when unnamed. */
  parinaam_rep_user_id: string | null;
  parinaam_rep_name: string | null;

  /** Who has still to confirm, or null when nobody has. Derived server-side. */
  awaiting: 'PARINAAM' | 'VOLUNTEER' | 'BOTH' | null;
  assigned_count: number;
  /** How many volunteers this session needs. Null means uncapped. */
  volunteers_needed: number | null;
  /** The communities this session serves, for the edit form's checkboxes. */
  community_ids: string[];

  activity_name: string;
  /** The activity's range — this session's own days are `start_date`/`end_date`. */
  activity_start_date: string;
  activity_end_date: string | null;
  activity_status: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  /** Expected length in whole minutes; null when not given. */
  planned_minutes: number | null;

  program_name: string;
}

export function getSession(sessionId: string): Promise<Session> {
  return apiGet<Session>(`/sessions/${sessionId}`);
}

/**
 * One session as a VOLUNTEER reads it: what the work is, not who is on it.
 *
 * A separate shape from `Session` rather than a slice of it. `getSession`
 * above is staff-only at the API because it carries the roster, and a type that
 * served both would invite a screen to reach for a field the volunteer's
 * endpoint never sends.
 */
export interface SessionOverview {
  session_id: string;
  session_name: string;
  session_description: string | null;
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  responsibility: 'PARINAAM' | 'VOLUNTEER' | 'COLLABORATIVE';
  status: 'DRAFT' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

  /** Null means uncapped, which is not the same as full. */
  volunteers_needed: number | null;
  assigned_count: number;

  activity_id: string;
  activity_name: string;
  activity_description: string | null;
  activity_location: string | null;
  activity_start_date: string;
  activity_end_date: string | null;
  activity_status: string;
  default_duration_minutes: number | null;

  program_id: string;
  program_name: string;
  program_description: string | null;
  program_start_date: string | null;
  program_end_date: string | null;
  program_status: string;

  communities: { name: string; city: string | null }[];
  parinaam_rep_name: string | null;
}

export function getSessionOverview(sessionId: string): Promise<SessionOverview> {
  return apiGet<SessionOverview>(`/sessions/${sessionId}/overview`);
}

/** The roster for one session — who is expected, and in what state. */
export function listSessionAssignments(sessionId: string): Promise<AssignmentDetail[]> {
  return apiGet<AssignmentDetail[]>(`/sessions/${sessionId}/assignments`);
}

/** Name the Parinaam team member responsible, or pass null to clear it. */
export function setSessionRepresentative(
  sessionId: string,
  userId: string | null
): Promise<Session> {
  return apiPut<Session>(`/sessions/${sessionId}/representative`, { user_id: userId });
}

/**
 * How many volunteers this session needs. Null clears the cap — it means no
 * limit, which is not the same as nobody.
 */
export function setSessionCapacity(
  sessionId: string,
  volunteersNeeded: number | null
): Promise<Session> {
  return apiPut<Session>(`/sessions/${sessionId}/capacity`, {
    volunteers_needed: volunteersNeeded,
  });
}

/** Correct the name, dates, hours, description or communities. */
export function updateSession(
  sessionId: string,
  changes: Record<string, unknown>
): Promise<Session> {
  return apiPatch<Session>(`/sessions/${sessionId}`, changes);
}

/** Remove a session nobody has used. Admin only; the reason is required. */
export function deleteSession(sessionId: string, reason: string): Promise<{ deleted: boolean }> {
  return apiDelete<{ deleted: boolean }>(`/sessions/${sessionId}`, { reason });
}

export function listActivitySessions(activityId: string): Promise<Session[]> {
  return apiGet<Session[]>(`/activities/${activityId}/sessions`);
}

/*
 * A type alias rather than an interface, like every other query type here:
 * `toQueryString` takes a `Record<string, QueryValue>`, and TypeScript gives
 * an object type alias an implicit index signature where an interface gets
 * none.
 */
export type SessionListQuery = {
  program_id?: string[];
  activity_id?: string[];
  page?: number;
  page_size?: number;
};

/**
 * Sessions across activities, for a picker.
 *
 * The sibling above needs an activity. This one does not, which is the whole
 * point of it: the attendance screen offers a session filter before anything
 * else has been narrowed.
 */
export function fetchSessions(params: SessionListQuery): Promise<Page<Session>> {
  return apiGetPage<Session>(`/sessions${toQueryString(params)}`);
}

/**
 * How a session reads in a dropdown.
 *
 * The name and the dates it runs. An activity with a single session is the
 * ordinary case, and there the name often repeats the activity heading
 * directly above — the dates are what tell two of them apart.
 */
export function sessionLabel(session: Session): string {
  const range =
    session.end_date && session.end_date !== session.start_date
      ? `${session.start_date} to ${session.end_date}`
      : session.start_date;
  return `${session.session_name} (${range})`;
}

/** What a confirmation did — enough to say something useful back. */
export interface ConfirmResult {
  session: Session;
  confirmed: 'PARINAAM' | 'VOLUNTEER';
  awaiting: 'PARINAAM' | 'VOLUNTEER' | null;
  completed: boolean;
}

/**
 * The signed-in volunteer's own sessions.
 *
 * Scoped to the account rather than to a parameter — there is deliberately no
 * way to ask the API for somebody else's list.
 */
export function listMySessions(): Promise<Session[]> {
  return apiGet<Session[]>('/sessions/mine');
}

export function confirmSession(sessionId: string): Promise<ConfirmResult> {
  return apiPost<ConfirmResult>(`/sessions/${sessionId}/confirm`, {});
}

/** Sessions this volunteer could sign up for. Never anybody else's roster. */
export function listOpenSessions(): Promise<Session[]> {
  return apiGet<Session[]>('/sessions/open');
}

/**
 * An admin setting a session's status directly.
 *
 * The only backwards status move in the system, which is why the reason is not
 * optional here, in the request schema, or in the database. It is the whole
 * account of why the derived answer — who confirmed what — was overruled.
 */
export function overrideSessionStatus(
  sessionId: string,
  status: Session['status'],
  reason: string
): Promise<Session> {
  return apiPost<Session>(`/sessions/${sessionId}/status`, { status, reason });
}

export function enrolInSession(sessionId: string): Promise<Session> {
  return apiPost<Session>(`/sessions/${sessionId}/enrol`, {});
}

/**
 * A session this volunteer has a history with.
 *
 * Feeds the volunteer-first attendance form. `last_attended` is what the list is
 * ordered by, and `assigned` distinguishes "they go here every week" from "they
 * are on the roster but have never been recorded" — which the form labels, so
 * staff can see why a suggestion is being made.
 */
export interface RecentSession extends Session {
  last_attended: string | null;
  assigned: boolean;
}

export function listRecentVolunteerSessions(volunteerId: string): Promise<RecentSession[]> {
  return apiGet<RecentSession[]>(`/volunteers/${volunteerId}/recent-sessions`);
}

/**
 * Add a session to an activity (D19).
 *
 * It answered with a phase id as well when it arrived, because creating a
 * session created the phase the roster named. There is no phase now, and the
 * screens refetch the activity's sessions rather than using the id directly.
 */
export interface CreatedSession {
  session_id: string;
}

export function createSession(
  activityId: string,
  body: CreateSessionBody
): Promise<CreatedSession> {
  return apiPost<CreatedSession>(`/activities/${activityId}/sessions`, body);
}
